/**
 * Read printed numbers off a still photo (no barcode) with Tesseract.js, entirely in the browser -
 * the image never leaves the device. Opt-in: the user taps "Read numbers from photo". The OCR text
 * is mined for digit runs matching our identifier shapes (kit 8-digit, member id, accession 5-digit,
 * USPS tracking ~22-digit); the user then taps a candidate to look it up and confirms the member.
 * A misread digit is expected, so this only ever SUGGESTS - it never auto-commits.
 */

/** Pure: pull plausible identifier number strings out of raw OCR text, best-guess first. Testable. */
export function extractNumberCandidates(text: string): string[] {
  if (!text) return [];
  const found = new Set<string>();

  // USPS tracking is usually printed in spaced/dashed groups; join them back to a single run.
  for (const m of text.match(/\d[\d\s-]{6,}\d/g) ?? []) {
    const digits = m.replace(/\D/g, '');
    if (digits.length >= 18 && digits.length <= 22) found.add(digits);
  }
  // Contiguous digit runs (kit / accession / member id).
  for (const r of text.match(/\d{5,}/g) ?? []) {
    if (r.length >= 5 && r.length <= 22) found.add(r);
  }

  const score = (s: string): number => {
    if (/^390\d{5}$/.test(s)) return 0; // full 390-form kit barcode
    if (s.length >= 20 && s.length <= 22) return 1; // USPS tracking
    if (s.length === 11) return 2; // zero-padded member id
    if (s.length === 8) return 3; // other 8-digit
    if (s.length === 5) return 5; // bare accession
    return 4; // 6-10 digit member id
  };

  return [...found].sort((a, b) => score(a) - score(b) || b.length - a.length).slice(0, 8);
}

/**
 * Recognize digit strings from an image. Lazy-loads Tesseract (a few MB of worker + WASM + lang from
 * its CDN on first use, then cached) so nothing is paid until the feature is used. Whitelisted to
 * digits + space for accuracy on number-only fields.
 */
export async function readNumbersFromImage(file: File, onProgress?: (pct: number) => void): Promise<string[]> {
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('eng', 1, {
    logger: (m: { status: string; progress: number }) => {
      if (onProgress && m.status === 'recognizing text') onProgress(Math.round(m.progress * 100));
    },
  });
  try {
    await worker.setParameters({ tessedit_char_whitelist: '0123456789 ' });
    const { data } = await worker.recognize(file);
    return extractNumberCandidates(data.text);
  } finally {
    await worker.terminate();
  }
}
